import { useEffect, useRef, useState, type RefObject } from "react"

/**
 * Mount/visibility state for the gallery's slide-up modals. The modals used to
 * stay permanently mounted and hide via `translateY(100%)` on a fixed-position
 * element — which SSR'd a duplicate copy of every image to crawlers, left the
 * off-screen Close button focusable (tab trap), and flashed unstyled modal
 * content before the widget CSS applied. Mounting only while open (plus an
 * exit delay matching the 400ms CSS transition) removes all three; `visible`
 * flips one frame after mount so the slide-in transition still runs.
 */
export function useModalPresence(isOpen: boolean, exitMs = 400) {
  const [mounted, setMounted] = useState(isOpen)
  const [visible, setVisible] = useState(false)
  const restoreFocusRef = useRef<HTMLElement | null>(null)

  // Focus round-trip: capture the trigger when the modal opens (the modal's
  // own close button takes focus via autoFocus), and hand focus back to the
  // trigger on close so keyboard users aren't dropped at document.body when
  // the focused close button unmounts.
  useEffect(() => {
    if (isOpen) {
      // Walk into shadow roots: widgets mount inside a shadow host, where
      // document.activeElement reports the HOST, not the clicked trigger.
      let el: Element | null = document.activeElement
      while (el && (el as HTMLElement).shadowRoot?.activeElement) {
        el = (el as HTMLElement).shadowRoot!.activeElement
      }
      restoreFocusRef.current = el as HTMLElement | null
      return
    }
    const trigger = restoreFocusRef.current
    restoreFocusRef.current = null
    // isConnected, not document.contains — contains() is false for shadow
    // content, which silently disabled restoration inside embeds.
    if (trigger && typeof trigger.focus === "function" && trigger.isConnected) {
      trigger.focus()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setMounted(true)
      // Track BOTH frame ids: cancelling only the outer frame lets an
      // already-scheduled inner callback flip `visible` back on after close,
      // which froze the modal open-looking until the unmount timer fired.
      let innerRaf = 0
      const outerRaf = requestAnimationFrame(() => {
        innerRaf = requestAnimationFrame(() => setVisible(true))
      })
      return () => {
        cancelAnimationFrame(outerRaf)
        cancelAnimationFrame(innerRaf)
      }
    }
    setVisible(false)
    // exitMs <= 0 means no exit animation to wait out: unmount synchronously
    // in this same effect pass. A deferred setTimeout(0) here left `mounted`
    // true across a close-then-reopen inside the same macrotask, so the
    // reopened overlay mounted before this effect's next run could reset it,
    // and the focus round-trip effect above captured the modal's own close
    // button (or lost the opener entirely) instead of the real trigger (#1560).
    if (exitMs <= 0) {
      setMounted(false)
      return
    }
    const timer = setTimeout(() => setMounted(false), exitMs)
    return () => clearTimeout(timer)
  }, [isOpen, exitMs])

  return { mounted, visible }
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * Keeps keyboard focus inside `containerRef` while `active` — Tab off the
 * last focusable element wraps to the first, Shift+Tab off the first wraps
 * to the last, and Tab fired while focus sits OUTSIDE the container entirely
 * (the "walked behind the modal" gap in #949) is pulled back to the first
 * focusable element instead of being left alone.
 *
 * Listens on `document` rather than the container: the event only ever
 * fires while one of the modal's own elements (or nothing) has focus, since
 * nothing outside the modal is reachable by Tab once this hook's own
 * "outside the container" branch below redirects it back in.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return

    // containerRef.current is read INSIDE the handler, not captured here at
    // effect-setup time: this effect's deps are `[active, containerRef]`, and
    // `active` flips true a render BEFORE the mount-gated modal (and its ref)
    // actually exists — same two-pass mount gotcha the scroll-into-view effect
    // below documents ("mounted must be a dep... ref null"). Reading fresh on
    // every keydown means a real Tab press — which can only happen after the
    // modal has actually finished mounting — always sees the attached node.
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const container = containerRef.current
      if (!container) return
      const focusable = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]

      // Same shadow-root walk as the restore-focus effect above — document
      // .activeElement reports the shadow HOST inside an embed, not the
      // element actually focused.
      let current: Element | null = document.activeElement
      while (current && (current as HTMLElement).shadowRoot?.activeElement) {
        current = (current as HTMLElement).shadowRoot!.activeElement
      }

      if (event.shiftKey) {
        if (current === first || !container.contains(current)) {
          event.preventDefault()
          last.focus()
        }
      } else if (current === last || !container.contains(current)) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [active, containerRef])
}
